// Hook pour gérer les types de licences (fetch, créer, supprimer, modifier, toggle actif)
import { useState, useCallback } from 'react';
import { LicenseType } from '../../../types/database';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

export function useLicenseTypes(onRefresh?: () => void) {
  const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([]);
  const [loading, setLoading] = useState(true);

  const loadLicenseTypes = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('license_types')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLicenseTypes(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des types de licences:', error);
      toast.error('Erreur lors du chargement des types de licences');
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteLicenseType = useCallback(async (licenseType: LicenseType) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le type de licence "${licenseType.name}" ?`)) {
      return;
    }

    try {
      // .select() : sans lui, une suppression bloquée par RLS ne renvoie ni erreur ni ligne
      const { data, error } = await supabase
        .from('license_types')
        .delete()
        .eq('id', licenseType.id)
        .select('id');

      if (error) throw error;
      if (!data?.length) throw new Error('Aucune ligne supprimée (droits insuffisants ?)');
      toast.success('Type de licence supprimé avec succès');
      await loadLicenseTypes();
      onRefresh?.();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  }, [loadLicenseTypes, onRefresh]);

  const toggleActive = useCallback(async (licenseType: LicenseType) => {
    try {
      const { data, error } = await supabase
        .from('license_types')
        .update({ is_active: !licenseType.is_active })
        .eq('id', licenseType.id)
        .select('id');

      if (error) throw error;
      if (!data?.length) throw new Error('Aucune ligne modifiée (droits insuffisants ?)');
      toast.success(`Type de licence ${!licenseType.is_active ? 'activé' : 'désactivé'} avec succès`);
      await loadLicenseTypes();
      onRefresh?.();
    } catch (error) {
      console.error('Erreur lors de la modification du statut:', error);
      toast.error('Erreur lors de la modification du statut');
    }
  }, [loadLicenseTypes, onRefresh]);

  return {
    licenseTypes,
    loading,
    loadLicenseTypes,
    deleteLicenseType,
    toggleActive
  };
}
